import { useState, useEffect, useMemo, useRef, memo } from 'react'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import VideoCallModal from './VideoCallModal'
import { MeetingRoom } from '../utils/videoCall'
import { getVideoCallStrategy } from '../utils/platformDetection'
import { getUserMeetings } from '../lib/meetingService'
import { MeetingListItem } from '../types/meeting'
import { useAuth } from '../hooks/useAuth'
import { useUserProfile } from '../hooks/useUserProfile'
import { getUserTimezone } from '../utils/timezoneUtils'
import ReviewModal from './ReviewModal'
import CancellationModal from './CancellationModal'
import CancellationSuccessModal from './CancellationSuccessModal'
import RescheduleMeetingModal from './RescheduleMeetingModal'
import RescheduleApprovalModal from './RescheduleApprovalModal'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { calculateHoursUntilMeeting, calculateCancellationPolicy } from '../lib/cancellationService'
import { canRescheduleMeeting, formatRescheduleRequestForDisplay } from '../lib/rescheduleService'
import { db } from '../lib/firebase'
import { RescheduleRequest, RescheduleRequestDisplay } from '../types/reschedule'
import BookingCalendar from './BookingCalendar'
import { getProfessionalById, Professional } from '../lib/professionalService'

// ─── Helper Icons (inline SVGs for design consistency) ───
const CalIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M8 3v4M16 3v4M3.5 10h17"/></svg>
)
const ClockIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
)
const VideoIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M23 7l-7 5 7 5z"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
)
const StarIcon = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/></svg>
)
const StarFilled = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/></svg>
)
const DotsIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/></svg>
)

// ─── Helpers ───
function getInitials(name: string): string {
  const parts = name.trim().split(' ')
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
  if (parts.length === 1 && parts[0].length > 0) return parts[0].substring(0, 2).toUpperCase()
  return 'U'
}

function getDateGroup(datetime: string, now: Date): 'today' | 'this-week' | 'next-week' | 'later' {
  const d = new Date(datetime)
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const endOfToday = new Date(startOfToday.getTime() + 86400000)
  if (d >= startOfToday && d < endOfToday) return 'today'
  const dayOfWeek = now.getDay()
  const endOfWeek = new Date(startOfToday.getTime() + (7 - dayOfWeek) * 86400000)
  if (d >= endOfToday && d < endOfWeek) return 'this-week'
  const endOfNextWeek = new Date(endOfWeek.getTime() + 7 * 86400000)
  if (d >= endOfWeek && d < endOfNextWeek) return 'next-week'
  return 'later'
}

function formatShortDate(datetime: string): string {
  const d = new Date(datetime)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${days[d.getDay()]} ${months[d.getMonth()]} ${d.getDate()}`
}

// ─── MeetingRow (extracted, stable component — won't unmount/remount on parent re-render) ───
interface MeetingRowProps {
  meeting: MeetingListItem
  isCancelled?: boolean
  status: string
  pillClass: string
  pillLabel: string
  joinable: boolean
  canRate: boolean
  countdown: string | null
  isCurrentUserClient: boolean
  pendingRequest: RescheduleRequest | null
  currentUserId?: string
  cancellingMeetingId: string | null
  canCancel: boolean
  canReschedule: boolean
  hasPendingReschedule: boolean
  onJoin: (meeting: MeetingListItem) => void
  onReview: (meeting: MeetingListItem) => void
  onCancel: (meeting: MeetingListItem) => void
  onReschedule: (meeting: MeetingListItem) => void
  onApproveReschedule: (request: RescheduleRequest) => void
  onBookAgain: (meeting: MeetingListItem) => void
}

const MeetingRow = memo(function MeetingRow({
  meeting, isCancelled = false, status, pillClass, pillLabel,
  joinable, canRate, countdown, isCurrentUserClient,
  pendingRequest, currentUserId, cancellingMeetingId,
  canCancel, canReschedule, hasPendingReschedule,
  onJoin, onReview, onCancel, onReschedule, onApproveReschedule, onBookAgain
}: MeetingRowProps) {
  const showActions = true

  return (
    <article className={`mtg flex items-center gap-5 mb-3 ${isCancelled ? 'opacity-70' : ''}`}>
      {/* Avatar */}
      {meeting.imageUrl ? (
        <div className="mtg-avatar"><img src={meeting.imageUrl} alt="" /></div>
      ) : (
        <div className="mtg-avatar">{getInitials(meeting.name)}</div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <div className={`text-[16px] font-semibold tracking-tight-half ${isCancelled ? 'text-[hsl(var(--muted-foreground))] line-through' : ''}`} style={isCancelled ? { textDecorationColor: 'hsl(var(--border))' } : undefined}>
            {meeting.name}
          </div>
          <span className={pillClass}><span className="dot"></span> {pillLabel}</span>

          {/* Review stars */}
          {status === 'completed' && meeting.clientReviewSubmitted && meeting.clientRating && (
            <span className="stars text-[13px]">
              {[1,2,3,4,5].map(i => (
                <span key={i} className={i <= (meeting.clientRating || 0) ? 'star' : 'star-empty'}>
                  <StarFilled size={13} />
                </span>
              ))}
            </span>
          )}
          {status === 'completed' && !meeting.clientReviewSubmitted && isCurrentUserClient && (
            <span className="text-[hsl(var(--muted-foreground))] text-[12.5px]">Not yet reviewed</span>
          )}
          {isCancelled && (
            <span className="text-[hsl(var(--muted-foreground))] text-[12.5px]">
              {meeting.amount ? `$${(meeting.amount / 100).toFixed(2)} refunded` : 'Full refund issued'}
            </span>
          )}
        </div>
        <div className="text-[hsl(var(--muted-foreground))] text-[13.5px] mt-0.5">{meeting.location}</div>
        <div className="mtg-meta">
          <span><CalIcon /> {status === 'scheduled' || status === 'in_progress' ? `${formatShortDate(meeting.datetime)} · ${meeting.time}` : `${meeting.date} · ${meeting.time}`}</span>
          <span className="sep">·</span>
          {(status === 'scheduled' || status === 'in_progress') && countdown && (
            <>
              <span><ClockIcon /> {countdown}</span>
              <span className="sep">·</span>
            </>
          )}
          <span>{meeting.duration} minutes</span>
          {(status === 'completed' || isCancelled) && meeting.amount > 0 && (
            <>
              <span className="sep">·</span>
              <span>${(meeting.amount / 100).toFixed(2)} {isCancelled ? 'refunded' : 'paid'}</span>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      {showActions && (
        <div className="flex items-center gap-2 shrink-0">
          {(status === 'scheduled' || status === 'in_progress') && (
            joinable ? (
              <button onClick={() => onJoin(meeting)} className="btn btn-primary !py-2 !px-3.5">
                <VideoIcon size={14} /> Join
              </button>
            ) : (
              <button className="btn btn-outline !py-2 !px-3.5" disabled style={{ opacity: .5, cursor: 'not-allowed' }} title="Joining opens 15 minutes before start.">
                <VideoIcon size={14} /> Join
              </button>
            )
          )}

          {canRate && (
            <button onClick={() => onReview(meeting)} className="btn btn-outline !py-2 !px-3.5">
              <StarIcon /> Leave a review
            </button>
          )}

          {status === 'completed' && isCurrentUserClient && (
            <button onClick={() => onBookAgain(meeting)} className="btn btn-ghost !py-2 !px-3">Book again</button>
          )}

          {isCancelled && isCurrentUserClient && (
            <button onClick={() => onBookAgain(meeting)} className="btn btn-outline !py-2 !px-3.5">Rebook</button>
          )}

          {pendingRequest && pendingRequest.requestedBy !== currentUserId && (
            <button onClick={() => onApproveReschedule(pendingRequest)} className="btn btn-primary !py-2 !px-3.5">
              Review request
            </button>
          )}

          {/* More menu — only for scheduled/in-progress (reschedule & cancel live here) */}
          {(status === 'scheduled' || status === 'in_progress') && (
            <Menu as="div" className="relative">
              <MenuButton className="btn btn-ghost !p-2" aria-label="More">
                <DotsIcon />
              </MenuButton>
              <MenuItems className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-xl overflow-hidden transition data-closed:scale-95 data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in" style={{ background: 'hsl(0 0% 8%)', border: '1px solid hsl(var(--border))', boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
                <div className="py-1">
                  <MenuItem>
                    {hasPendingReschedule ? (
                      <span className="block px-4 py-2 text-[14px] text-[hsl(var(--muted-foreground))] cursor-not-allowed">Reschedule Pending</span>
                    ) : canReschedule ? (
                      <button onClick={() => onReschedule(meeting)} className="w-full text-left block px-4 py-2 text-[14px] text-[hsl(var(--foreground))] hover:bg-white/5">Reschedule</button>
                    ) : (
                      <span className="block px-4 py-2 text-[14px] text-[hsl(var(--muted-foreground))] cursor-not-allowed">Cannot Reschedule</span>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {canCancel ? (
                      <button onClick={() => onCancel(meeting)} disabled={cancellingMeetingId === meeting.id} className="w-full text-left block px-4 py-2 text-[14px] text-[#ff8a73] hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed">
                        {cancellingMeetingId === meeting.id ? 'Cancelling...' : 'Cancel Meeting'}
                      </button>
                    ) : (
                      <span className="block px-4 py-2 text-[14px] text-[hsl(var(--muted-foreground))] cursor-not-allowed">Cannot Cancel</span>
                    )}
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>
          )}
        </div>
      )}
    </article>
  )
})

// ─── Main Component ───
export default function Meetings() {
  const { user } = useAuth()
  const { userProfile } = useUserProfile()
  const [showVideoCall, setShowVideoCall] = useState(false)
  const [currentMeeting, setCurrentMeeting] = useState<MeetingListItem | null>(null)
  const [meetingRoom, setMeetingRoom] = useState<MeetingRoom | null>(null)
  const [meetings, setMeetings] = useState<MeetingListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [reviewMeeting, setReviewMeeting] = useState<MeetingListItem | null>(null)
  const [pastMeetingsPage, setPastMeetingsPage] = useState(1)
  const [showAllPastMeetings] = useState(false)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [cancellingMeeting, setCancellingMeeting] = useState<string | null>(null)
  const [showCancellationModal, setShowCancellationModal] = useState(false)
  const [cancellationMeeting, setCancellationMeeting] = useState<MeetingListItem | null>(null)
  const [cancellationPolicyMessage, setCancellationPolicyMessage] = useState('')
  const [showCancellationSuccess, setShowCancellationSuccess] = useState(false)
  const [cancellationResult, setCancellationResult] = useState<{ refundAmount?: string; compensationAmount?: string; message?: string } | null>(null)
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)
  const [rescheduleMeeting, setRescheduleMeeting] = useState<MeetingListItem | null>(null)
  const [rescheduleRequests, setRescheduleRequests] = useState<Record<string, RescheduleRequest>>({})
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [approvalRequest, setApprovalRequest] = useState<RescheduleRequestDisplay | null>(null)
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null)
  const [pastFilter, setPastFilter] = useState<'all' | 'reviewed' | 'not-reviewed'>('all')
  const [showBookingCalendar, setShowBookingCalendar] = useState(false)
  const [bookingProfessional, setBookingProfessional] = useState<Professional | null>(null)

  // Section refs for scroll-to
  const upcomingRef = useRef<HTMLElement>(null)
  const pastRef = useRef<HTMLElement>(null)
  const cancelledRef = useRef<HTMLElement>(null)

  const scrollToSection = (ref: React.RefObject<HTMLElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // ─── Status helpers (pure functions of time) ───
  const getTimeBasedStatus = (meeting: MeetingListItem) => {
    const meetingDate = new Date(meeting.datetime)
    const meetingEndTime = new Date(meetingDate.getTime() + (meeting.duration * 60 * 1000))
    if (meeting.status === 'cancelled' || meeting.status === 'completed' || meeting.status === 'no_show') return meeting.status
    if (currentTime < meetingDate) return 'scheduled'
    if (currentTime >= meetingDate && currentTime <= meetingEndTime) return 'in_progress'
    return 'completed'
  }

  const canJoinMeeting = (meeting: MeetingListItem) => {
    const meetingDate = new Date(meeting.datetime)
    const canJoinTime = new Date(meetingDate.getTime() - 15 * 60 * 1000)
    const meetingEndTime = new Date(meetingDate.getTime() + meeting.duration * 60 * 1000)
    return meeting.status === 'scheduled' && currentTime >= canJoinTime && currentTime <= meetingEndTime
  }

  const isStartingSoon = (meeting: MeetingListItem) => {
    const meetingDate = new Date(meeting.datetime)
    const diffMs = meetingDate.getTime() - currentTime.getTime()
    return diffMs > 0 && diffMs <= 15 * 60 * 1000 && meeting.status === 'scheduled'
  }

  // ─── Data Loading ───
  useEffect(() => {
    const loadMeetings = async () => {
      if (!user) { setLoading(false); return }
      if (userProfile === null) { setLoading(true); return }
      try {
        setLoading(true)
        const userTimezone = userProfile ? getUserTimezone(userProfile.timezone) : undefined
        const allMeetings = await getUserMeetings(user.uid, undefined, userTimezone)
        setMeetings(allMeetings)
      } catch (error) {
        console.error('Error loading meetings:', error)
      } finally {
        setLoading(false)
      }
    }
    loadMeetings()
  }, [user, userProfile])

  // Smart timer: always 1s tick for live countdown, but only updates state every 30s when no countdown is active
  useEffect(() => {
    let lastSlowTick = Date.now()

    const timer = setInterval(() => {
      const now = Date.now()
      const needsFastTick = meetings.some(m => {
        if (m.status !== 'scheduled') return false
        const diff = new Date(m.datetime).getTime() - now
        return diff > 0 && diff <= 15 * 60 * 1000
      })

      if (needsFastTick) {
        setCurrentTime(new Date())
      } else if (now - lastSlowTick >= 30000) {
        lastSlowTick = now
        setCurrentTime(new Date())
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [meetings])

  // Fetch reschedule requests
  const fetchRescheduleRequests = async () => {
    if (!user) return
    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore')
      const requestsRef = collection(db, 'userRescheduleRequests', user.uid, 'requests')
      const q = query(requestsRef, where('status', '==', 'pending'))
      const querySnapshot = await getDocs(q)
      const requests: Record<string, RescheduleRequest> = {}
      querySnapshot.forEach((doc) => {
        const data = doc.data() as RescheduleRequest
        requests[doc.id] = { ...data, id: doc.id }
      })
      setRescheduleRequests(requests)
    } catch (error) {
      console.error('Error fetching reschedule requests:', error)
    }
  }

  useEffect(() => { fetchRescheduleRequests() }, [user])

  // ─── Countdown helpers ───
  const formatCountdown = (meeting: MeetingListItem) => {
    if (meeting.status === 'cancelled') return null
    const diffMs = new Date(meeting.datetime).getTime() - currentTime.getTime()
    if (diffMs <= 0) return null
    const d = Math.floor(diffMs / 86400000)
    const h = Math.floor((diffMs % 86400000) / 3600000)
    const m = Math.floor((diffMs % 3600000) / 60000)
    const s = Math.floor((diffMs % 60000) / 1000)
    if (d > 0) return `In ${d}d ${h}h`
    if (h > 0) return `In ${h}h ${m}m`
    return `In ${m}m ${s}s`
  }

  const formatCountdownHero = (meeting: MeetingListItem) => {
    const diffMs = new Date(meeting.datetime).getTime() - currentTime.getTime()
    if (diffMs <= 0) return { min: 0, sec: 0 }
    return { min: Math.floor(diffMs / 60000), sec: Math.floor((diffMs % 60000) / 1000) }
  }

  // ─── Handlers ───
  const handleJoinMeeting = (meeting: MeetingListItem) => {
    (async () => {
      try {
        if (!meeting.meetingRoomUrl) throw new Error('Meeting room not available. Please contact support.')
        const isHost = user?.uid === meeting.professionalId
        const currentUserName = user?.displayName || user?.email || 'Current User'
        const strategy = getVideoCallStrategy()
        switch (strategy) {
          case 'popup': {
            const url = `/video-call?roomUrl=${encodeURIComponent(meeting.meetingRoomUrl)}&userName=${encodeURIComponent(currentUserName)}&professionalName=${encodeURIComponent(meeting.name)}&isHost=${isHost}`
            if (!window.open(url, 'videoCall', 'width=1200,height=800,resizable=yes,scrollbars=yes,status=yes,menubar=no,toolbar=no')) alert('Please allow popups for this site to join the video call.')
            break
          }
          case 'navigation':
            window.location.href = `/video-call?roomUrl=${encodeURIComponent(meeting.meetingRoomUrl)}&userName=${encodeURIComponent(currentUserName)}&professionalName=${encodeURIComponent(meeting.name)}&isHost=${isHost}`
            break
          default:
            setCurrentMeeting(meeting)
            setMeetingRoom({ url: meeting.meetingRoomUrl, roomName: meeting.meetingRoomId || 'video-room', created: new Date().toISOString() })
            setShowVideoCall(true)
        }
      } catch (error) {
        alert(`Failed to join meeting: ${error instanceof Error ? error.message : 'Unknown error'}`)
      }
    })()
  }

  const handleReviewMeeting = (meeting: MeetingListItem) => { setReviewMeeting(meeting); setShowReviewModal(true) }
  const handleCloseVideoCall = () => { setShowVideoCall(false); setCurrentMeeting(null); setMeetingRoom(null) }
  const handleCloseReviewModal = () => { setShowReviewModal(false); setReviewMeeting(null) }

  const handleReviewSubmitted = () => {
    if (user) {
      const tz = userProfile ? getUserTimezone(userProfile.timezone) : undefined
      getUserMeetings(user.uid, undefined, tz, true).then(setMeetings)
    }
    handleCloseReviewModal()
  }

  const canUserRate = (meeting: MeetingListItem) => {
    if (!user) return false
    if (getTimeBasedStatus(meeting) !== 'completed') return false
    if (user.uid === meeting.professionalId) return false
    return !meeting.clientReviewSubmitted
  }

  const canCancelMeeting = (meeting: MeetingListItem) => {
    if (getTimeBasedStatus(meeting) !== 'scheduled') return false
    return currentTime < new Date(meeting.datetime)
  }

  const openCancellationModal = (meeting: MeetingListItem) => {
    if (!user) return
    const hoursUntil = calculateHoursUntilMeeting(new Date(meeting.datetime))
    const cancelledBy = user.uid === meeting.clientId ? 'client' : 'professional'
    const policy = calculateCancellationPolicy(hoursUntil, cancelledBy)
    let msg = ''
    if (cancelledBy === 'professional') {
      msg = 'The client will receive a full refund.\n\nYou will not receive any compensation for this session.'
    } else if (policy.refundPercent === 100) {
      msg = policy.stripeFeeHandling === 'client'
        ? 'You will receive a refund minus Stripe processing fees (~3%).\n\nThe professional will not receive compensation.'
        : 'You will receive a full refund.\n\nThe professional will not receive compensation.'
    } else if (policy.refundPercent === 50) {
      msg = 'You will receive a 50% refund (minus Stripe processing fees).\n\nThe professional will receive 50% compensation.'
    } else {
      msg = 'No refund is available for cancellations less than 24 hours before the meeting.\n\nThe professional will receive full compensation.'
    }
    setCancellationMeeting(meeting); setCancellationPolicyMessage(msg); setShowCancellationModal(true)
  }

  const handleCancelMeeting = async (reason?: string) => {
    if (!user || !cancellationMeeting) return
    try {
      setCancellingMeeting(cancellationMeeting.id)
      const functions = getFunctions()
      const processCancellation = httpsCallable(functions, 'processCancellation')
      const result = await processCancellation({ meetingId: cancellationMeeting.id, reason: reason || 'User requested cancellation', idempotencyKey: `cancel_${cancellationMeeting.id}_${user.uid}_${Date.now()}` })
      const data = result.data as { success: boolean; refundAmount?: string; professionalCompensation?: number; message?: string }
      if (data.success) {
        const role = user.uid === cancellationMeeting.clientId ? 'client' : 'professional'
        setCancellationResult({ refundAmount: role === 'client' ? (data.refundAmount || '$0.00') : undefined, compensationAmount: role === 'professional' ? (data.professionalCompensation ? `$${data.professionalCompensation}` : '$0.00') : undefined, message: data.message })
        setShowCancellationModal(false); setShowCancellationSuccess(true)
        const tz = userProfile ? getUserTimezone(userProfile.timezone) : undefined
        setMeetings(await getUserMeetings(user.uid, undefined, tz))
      } else { throw new Error(data.message || 'Cancellation failed') }
    } catch (error) {
      alert(`Failed to cancel meeting: ${error instanceof Error ? error.message : 'Unknown error'}`)
      setShowCancellationModal(false)
    } finally { setCancellingMeeting(null) }
  }

  const openRescheduleModal = (meeting: MeetingListItem) => { setRescheduleMeeting(meeting); setShowRescheduleModal(true) }

  const openApprovalModal = (request: RescheduleRequest) => {
    setApprovalRequest(formatRescheduleRequestForDisplay(request)); setShowApprovalModal(true)
  }

  const getPendingRescheduleRequest = (meetingId: string): RescheduleRequest | null => {
    return Object.values(rescheduleRequests).find(req => req.meetingId === meetingId) || null
  }

  const handleRescheduleRequested = async () => {
    if (!user) return
    setToastMessage({ type: 'success', message: 'Reschedule request sent! The other participant will be notified.' })
    setTimeout(() => setToastMessage(null), 5000)
    const tz = userProfile ? getUserTimezone(userProfile.timezone) : undefined
    setMeetings(await getUserMeetings(user.uid, undefined, tz))
    await fetchRescheduleRequests()
  }

  const handleBookAgain = async (meeting: MeetingListItem) => {
    try {
      // Determine the professional ID (the other participant)
      const professionalId = user?.uid === meeting.clientId ? meeting.professionalId : meeting.clientId
      const professional = await getProfessionalById(professionalId)
      if (professional) {
        setBookingProfessional(professional)
        setShowBookingCalendar(true)
      }
    } catch (error) {
      console.error('Error opening booking calendar:', error)
    }
  }

  // ─── Derived Data ───
  const upcomingMeetings = useMemo(() =>
    meetings.filter(m => { const s = getTimeBasedStatus(m); return s === 'scheduled' || s === 'in_progress' })
      .sort((a, b) => {
        const aS = getTimeBasedStatus(a); const bS = getTimeBasedStatus(b)
        if (aS === 'in_progress' && bS !== 'in_progress') return -1
        if (bS === 'in_progress' && aS !== 'in_progress') return 1
        return new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
      }), [meetings, currentTime])

  const cancelledMeetings = useMemo(() =>
    meetings.filter(m => getTimeBasedStatus(m) === 'cancelled').sort((a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime()),
    [meetings, currentTime])

  const allPastMeetings = useMemo(() =>
    meetings.filter(m => { const s = getTimeBasedStatus(m); return s === 'completed' || s === 'no_show' })
      .sort((a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime()),
    [meetings, currentTime])

  const filteredPastMeetings = useMemo(() => {
    if (pastFilter === 'all') return allPastMeetings
    if (pastFilter === 'reviewed') return allPastMeetings.filter(m => m.clientReviewSubmitted)
    return allPastMeetings.filter(m => !m.clientReviewSubmitted)
  }, [allPastMeetings, pastFilter])

  const PAST_MEETINGS_PER_PAGE = 10
  const pastMeetings = showAllPastMeetings ? filteredPastMeetings : filteredPastMeetings.slice(0, pastMeetingsPage * PAST_MEETINGS_PER_PAGE)

  const startingSoonMeeting = useMemo(() => upcomingMeetings.find(m => isStartingSoon(m) || canJoinMeeting(m)), [upcomingMeetings, currentTime])

  const groupedUpcoming = useMemo(() => {
    const remaining = upcomingMeetings.filter(m => m.id !== startingSoonMeeting?.id)
    const today: MeetingListItem[] = [], thisWeek: MeetingListItem[] = [], nextWeek: MeetingListItem[] = [], later: MeetingListItem[] = []
    remaining.forEach(m => { const g = getDateGroup(m.datetime, currentTime); if (g === 'today') today.push(m); else if (g === 'this-week') thisWeek.push(m); else if (g === 'next-week') nextWeek.push(m); else later.push(m) })
    const groups: { label: string; meetings: MeetingListItem[] }[] = []
    if (today.length) groups.push({ label: 'Today', meetings: today })
    if (thisWeek.length) groups.push({ label: 'This week', meetings: thisWeek })
    if (nextWeek.length) groups.push({ label: 'Next week', meetings: nextWeek })
    if (later.length) groups.push({ label: 'Later', meetings: later })
    return groups
  }, [upcomingMeetings, startingSoonMeeting, currentTime])

  const tzDisplay = useMemo(() => {
    if (!userProfile?.timezone) return null
    try { const tz = getUserTimezone(userProfile.timezone); const abbr = new Date().toLocaleTimeString('en-US', { timeZone: tz, timeZoneName: 'short' }).split(' ').pop(); return `${tz.replace(/_/g, '/')} · ${abbr}` }
    catch { return null }
  }, [userProfile])

  // ─── Row props builder (computes per-row props from current state) ───
  const buildRowProps = (meeting: MeetingListItem, isCancelled = false) => {
    const status = getTimeBasedStatus(meeting)
    const pendingRequest = getPendingRescheduleRequest(meeting.id)
    let pillClass = 'pill', pillLabel = ''
    if (status === 'in_progress') { pillClass += ' pill-live'; pillLabel = 'Live' }
    else if (status === 'scheduled') { pillClass += isStartingSoon(meeting) ? ' pill-soon' : ' pill-scheduled'; pillLabel = isStartingSoon(meeting) ? 'Starting soon' : 'Scheduled' }
    else if (status === 'completed') { pillClass += ' pill-completed'; pillLabel = 'Completed' }
    else if (status === 'cancelled') { pillClass += ' pill-cancelled'; pillLabel = 'Cancelled' }
    if (pendingRequest) { pillClass = 'pill pill-soon'; pillLabel = pendingRequest.requestedBy === user?.uid ? 'Reschedule sent' : 'Reschedule requested' }

    const canRescheduleResult = canRescheduleMeeting({ status: meeting.status, datetime: meeting.datetime, rescheduleCount: (meeting as any).rescheduleCount, rescheduleRequestId: (meeting as any).rescheduleRequestId })

    return {
      meeting, isCancelled, status, pillClass, pillLabel,
      joinable: canJoinMeeting(meeting),
      canRate: canUserRate(meeting),
      countdown: formatCountdown(meeting),
      isCurrentUserClient: user?.uid !== meeting.professionalId,
      pendingRequest,
      currentUserId: user?.uid,
      cancellingMeetingId: cancellingMeeting,
      canCancel: canCancelMeeting(meeting),
      canReschedule: canRescheduleResult.canReschedule,
      hasPendingReschedule: !!pendingRequest,
      onJoin: handleJoinMeeting,
      onReview: handleReviewMeeting,
      onCancel: openCancellationModal,
      onReschedule: openRescheduleModal,
      onApproveReschedule: openApprovalModal,
      onBookAgain: handleBookAgain,
    }
  }

  // ─── Render ───
  if (loading) {
    return (
      <div className="py-20 flex flex-col items-center justify-center gap-3">
        <div className="w-6 h-6 rounded-full border-2 border-white/20 border-t-white animate-spin" />
        <span className="text-[14px] text-[hsl(var(--muted-foreground))]">Loading meetings...</span>
      </div>
    )
  }

  return (
    <div className="animate-fade-in-up-fast max-w-[1180px]">
      {/* Header */}
      <header className="flex items-end justify-between gap-6 mb-7">
        <div>
          <h1 className="text-[36px] md:text-[44px] leading-[1.05] font-medium tracking-tight-1">
            Your <span className="font-serif italic font-normal">meetings</span>
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2 text-[15px] max-w-2xl">
            {meetings.length === 0 ? 'No sessions scheduled yet.' : (
              <>{upcomingMeetings.length} session{upcomingMeetings.length !== 1 ? 's' : ''} scheduled.{upcomingMeetings.length > 0 && formatCountdown(upcomingMeetings[0]) && <> The next one is in <span className="text-[hsl(var(--foreground))]/90">{formatCountdown(upcomingMeetings[0])?.toLowerCase()}</span>.</>}</>
            )}
          </p>
        </div>
      </header>

      {/* Navigation (scroll-to links) */}
      <div className="flex items-center justify-between border-b mb-9 sticky top-0 z-10 pb-px" style={{ borderColor: 'hsl(var(--border) / 0.5)', background: 'hsl(var(--background))' }}>
        <div className="flex items-center gap-7">
          <button className="mtg-nav mtg-nav-active" onClick={() => scrollToSection(upcomingRef)}>Upcoming <span className="count">{upcomingMeetings.length}</span></button>
          <button className="mtg-nav" onClick={() => scrollToSection(pastRef)}>Past <span className="count">{allPastMeetings.length}</span></button>
          <button className="mtg-nav" onClick={() => scrollToSection(cancelledRef)}>Cancelled <span className="count">{cancelledMeetings.length}</span></button>
        </div>
        {tzDisplay && <div className="hidden md:flex items-center gap-2 text-[13px] text-[hsl(var(--muted-foreground))]">Times in <span className="text-[hsl(var(--foreground))]/90 font-medium">{tzDisplay}</span></div>}
      </div>

      {/* ═══ UPCOMING ═══ */}
      <section ref={upcomingRef} className="scroll-mt-16 mb-16">
        {upcomingMeetings.length === 0 ? (
          <div className="mtg-empty">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-[hsl(var(--muted-foreground))] mb-4"><rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M8 3v4M16 3v4M3.5 10h17"/></svg>
            <div className="text-[16px] font-medium mb-1">No upcoming meetings</div>
            <p className="text-[14px] text-[hsl(var(--muted-foreground))]">Your scheduled sessions will appear here.</p>
          </div>
        ) : (
          <>
            {/* Starting Soon Hero */}
            {startingSoonMeeting && (() => {
              const heroMeetingDate = new Date(startingSoonMeeting.datetime)
              const isLive = currentTime >= heroMeetingDate
              const heroCountdown = formatCountdownHero(startingSoonMeeting)
              return (
                <div className="next-up p-7 mb-9">
                  <div className="next-up-content grid lg:grid-cols-[1fr_auto] gap-8 items-center">
                    <div className="flex items-start gap-5 min-w-0">
                      {startingSoonMeeting.imageUrl ? (
                        <div className="mtg-avatar mtg-avatar-lg" style={{ animation: 'pulse-ring 1.6s ease-out infinite' }}><img src={startingSoonMeeting.imageUrl} alt="" /></div>
                      ) : (
                        <div className="mtg-avatar mtg-avatar-lg" style={{ animation: 'pulse-ring 1.6s ease-out infinite' }}>{getInitials(startingSoonMeeting.name)}</div>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap mb-1">
                          <span className={isLive ? 'pill pill-live' : 'pill pill-soon'}><span className="dot"></span> {isLive ? 'Live now' : 'Starting soon'}</span>
                          <span className="text-[hsl(var(--muted-foreground))] text-[11.5px]">Joining opens 15 minutes before the call.</span>
                        </div>
                        <div className="text-[22px] font-medium tracking-tight-half leading-tight">{startingSoonMeeting.name}</div>
                        <div className="text-[hsl(var(--muted-foreground))] text-[13px] mt-0.5">{startingSoonMeeting.location}</div>
                        <div className="mtg-meta mt-3">
                          <span><CalIcon /> Today · {startingSoonMeeting.time}</span>
                          <span className="sep">·</span>
                          <span><ClockIcon /> {startingSoonMeeting.duration} minutes</span>
                          <span className="sep">·</span>
                          <span><VideoIcon /> Wonderstood video</span>
                        </div>

                        {/* Description / prep notes (written by the client at booking time) */}
                        {startingSoonMeeting.description && (
                          <div className="mt-5 rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(var(--border) / 0.5)' }}>
                            <div className="text-[11px] tracking-[3px] uppercase text-[hsl(var(--muted-foreground))] mb-2">
                              {user?.uid === startingSoonMeeting.clientId ? 'You wrote' : `${startingSoonMeeting.name} wrote`}
                            </div>
                            <p className="text-[hsl(var(--foreground))]/90 text-[13px] leading-relaxed">{startingSoonMeeting.description}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right column: countdown + actions */}
                    <div className="flex flex-col items-stretch gap-4 lg:w-[260px]">
                      <div className="text-center">
                        {isLive ? (
                          <>
                            <div className="text-[11px] tracking-[3px] uppercase text-[hsl(var(--muted-foreground))]">Session is</div>
                            <div className="font-medium tracking-tight-1 mt-1 leading-none flex items-baseline justify-center gap-1">
                              <span className="text-[44px]">Live</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-[11px] tracking-[3px] uppercase text-[hsl(var(--muted-foreground))]">Starts in</div>
                            <div className="font-medium tracking-tight-1 mt-1 leading-none flex items-baseline justify-center gap-1">
                              <span className="text-[44px]">{heroCountdown.min}</span>
                              <span className="text-[hsl(var(--muted-foreground))] text-[17px]">min</span>
                              <span className="text-[44px]">{heroCountdown.sec}</span>
                              <span className="text-[hsl(var(--muted-foreground))] text-[17px]">s</span>
                            </div>
                          </>
                        )}
                      </div>
                      <button onClick={() => handleJoinMeeting(startingSoonMeeting)} disabled={!canJoinMeeting(startingSoonMeeting)} className="btn btn-primary justify-center !py-3.5 text-[14px] disabled:opacity-50 disabled:cursor-not-allowed">
                        <VideoIcon size={15} /> Join session
                      </button>
                      <button className="btn btn-ghost justify-center text-[12px]" onClick={() => openRescheduleModal(startingSoonMeeting)}>
                        <ClockIcon size={12} /> Reschedule
                      </button>
                    </div>
                  </div>
                </div>
              )
            })()}

            {/* Grouped upcoming */}
            {groupedUpcoming.map(group => (
              <div key={group.label}>
                <div className="date-divider"><span className="lbl">{group.label}</span><span className="line"></span></div>
                {group.meetings.map(m => <MeetingRow key={m.id} {...buildRowProps(m)} />)}
              </div>
            ))}
          </>
        )}
      </section>

      {/* ═══ PAST ═══ */}
      <section ref={pastRef} className="scroll-mt-16 mb-16">
        <div className="flex items-end justify-between mb-5">
          <div>
            <h2 className="text-[24px] font-medium tracking-tight-half">Past meetings</h2>
            <p className="text-[hsl(var(--muted-foreground))] text-[14px] mt-1">A record of every session you've taken.</p>
          </div>
          {allPastMeetings.length > 0 && (
            <div className="flex items-center gap-1.5">
              {(['all', 'reviewed', 'not-reviewed'] as const).map(f => (
                <button key={f} className={`btn !py-1.5 !px-3 ${pastFilter === f ? 'btn-outline' : 'btn-ghost'}`} style={pastFilter === f ? { background: 'rgba(255,255,255,0.05)', boxShadow: 'inset 0 0 0 1px hsl(var(--foreground) / 0.45)' } : undefined} onClick={() => setPastFilter(f)}>
                  {f === 'all' ? 'All' : f === 'reviewed' ? 'Reviewed' : 'Not yet reviewed'}
                </button>
              ))}
            </div>
          )}
        </div>
        {pastMeetings.length === 0 ? (
          <div className="mtg-empty"><div className="text-[16px] font-medium mb-1">No past meetings</div><p className="text-[14px] text-[hsl(var(--muted-foreground))]">Completed sessions will appear here.</p></div>
        ) : (
          <>
            {pastMeetings.map(m => <MeetingRow key={m.id} {...buildRowProps(m)} />)}
            {filteredPastMeetings.length > PAST_MEETINGS_PER_PAGE && !showAllPastMeetings && pastMeetings.length < filteredPastMeetings.length && (
              <div className="flex items-center justify-center mt-8">
                <button className="btn btn-outline" onClick={() => setPastMeetingsPage(p => p + 1)}>
                  Show {Math.min(PAST_MEETINGS_PER_PAGE, filteredPastMeetings.length - pastMeetings.length)} more
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* ═══ CANCELLED ═══ */}
      <section ref={cancelledRef} className="scroll-mt-16 mb-16">
        <div className="mb-5">
          <h2 className="text-[24px] font-medium tracking-tight-half">Cancelled</h2>
          <p className="text-[hsl(var(--muted-foreground))] text-[14px] mt-1">Sessions that were cancelled or refunded.</p>
        </div>
        {cancelledMeetings.length === 0 ? (
          <div className="mtg-empty"><div className="text-[16px] font-medium mb-1">No cancelled meetings</div><p className="text-[14px] text-[hsl(var(--muted-foreground))]">Cancelled sessions will appear here.</p></div>
        ) : (
          cancelledMeetings.map(m => <MeetingRow key={m.id} {...buildRowProps(m, true)} />)
        )}
      </section>

      {/* ─── Modals ─── */}
      {showVideoCall && currentMeeting && meetingRoom && <VideoCallModal open={showVideoCall} onClose={handleCloseVideoCall} roomUrl={meetingRoom.url} userName={user?.displayName || 'Current User'} isHost={false} />}
      {showReviewModal && reviewMeeting && user && <ReviewModal open={showReviewModal} onClose={handleCloseReviewModal} meeting={reviewMeeting} currentUserId={user.uid} onReviewSubmitted={handleReviewSubmitted} />}
      {cancellationMeeting && <CancellationModal open={showCancellationModal} onClose={() => { setShowCancellationModal(false); setCancellationMeeting(null) }} onConfirm={handleCancelMeeting} policyMessage={cancellationPolicyMessage} meetingTitle={cancellationMeeting.name} meetingDateTime={new Date(cancellationMeeting.datetime).toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })} isProcessing={cancellingMeeting === cancellationMeeting.id} />}
      {user && cancellationMeeting && cancellationResult && <CancellationSuccessModal open={showCancellationSuccess} onClose={() => { setShowCancellationSuccess(false); setCancellationMeeting(null); setCancellationResult(null) }} userRole={user.uid === cancellationMeeting.clientId ? 'client' : 'professional'} refundAmount={cancellationResult.refundAmount} compensationAmount={cancellationResult.compensationAmount} message={cancellationResult.message} />}
      {showRescheduleModal && rescheduleMeeting && user && <RescheduleMeetingModal open={showRescheduleModal} onClose={() => { setShowRescheduleModal(false); setRescheduleMeeting(null) }} meeting={rescheduleMeeting} onRescheduleRequested={handleRescheduleRequested} onError={(msg) => { setToastMessage({ type: 'error', message: msg }); setTimeout(() => setToastMessage(null), 5000) }} />}
      {showApprovalModal && approvalRequest && user && <RescheduleApprovalModal open={showApprovalModal} onClose={() => { setShowApprovalModal(false); setApprovalRequest(null) }} request={approvalRequest} currentUserId={user.uid} onResponseSubmitted={async () => { setShowApprovalModal(false); setApprovalRequest(null); const tz = userProfile ? getUserTimezone(userProfile.timezone) : undefined; setMeetings(await getUserMeetings(user.uid, undefined, tz)); await fetchRescheduleRequests() }} />}
      <BookingCalendar open={showBookingCalendar} onClose={() => { setShowBookingCalendar(false); setBookingProfessional(null) }} professional={bookingProfessional} onNavigateToCalendar={() => { setShowBookingCalendar(false); setBookingProfessional(null) }} />

      {/* Toast */}
      {toastMessage && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className="rounded-xl px-5 py-3.5 flex items-center gap-3 max-w-md" style={{ background: 'hsl(0 0% 8%)', border: '1px solid hsl(var(--border))', boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
            {toastMessage.type === 'success'
              ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--foreground))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ff8a73" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>}
            <p className="text-[14px]" style={{ color: toastMessage.type === 'success' ? 'hsl(var(--foreground))' : '#ff8a73' }}>{toastMessage.message}</p>
            <button onClick={() => setToastMessage(null)} className="ml-auto text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
